import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Trash2, 
  CheckCircle, 
  Eye, 
  RefreshCw
} from 'lucide-react';

const GymList = () => {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [planTier, setPlanTier] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(15);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchGyms = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');

      let url = `${API_BASE_URL}/api/admin/gyms?page=${page}&perPage=${perPage}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      if (planTier) url += `&planTier=${encodeURIComponent(planTier)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve gyms list.');
      }

      const result = await response.json();
      if (result.success) {
        setGyms(result.data);
        setTotal(result.pagination.total);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API. Showing offline dummy records.');
      // Load fallback simulation data
      setGyms(getMockGyms());
      setTotal(getMockGyms().length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGyms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, planTier]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchGyms();
  };

  const getMockGyms = () => {
    return [
      { id: '1', name: 'Muscle Factory', code: 'MUS-1029', ownerName: 'Rajesh Kumar', ownerEmail: 'rajesh@gmail.com', ownerPhone: '9876543210', city: 'Hyderabad', planTier: 'pro', status: 'active', membersCount: 145, lastCheckIn: new Date().toISOString() },
      { id: '2', name: 'Iron House', code: 'IRO-8839', ownerName: 'Priya Sharma', ownerEmail: 'priya@gmail.com', ownerPhone: '8876543211', city: 'Vijayawada', planTier: 'starter', status: 'trial', membersCount: 52, lastCheckIn: new Date(Date.now() - 3600000).toISOString() },
      { id: '3', name: 'FitZone', code: 'FIT-3829', ownerName: 'Sanjay Reddy', ownerEmail: 'sanjay@gmail.com', ownerPhone: '7876543212', city: 'Guntur', planTier: 'free', status: 'suspended', membersCount: 12, lastCheckIn: new Date(Date.now() - 86400000 * 8).toISOString() }
    ];
  };

  const handleToggleStatus = async (gymId, currentStatus) => {
    const nextStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE_URL}/api/admin/gyms/${gymId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: nextStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update gym status.');
      }

      // Refresh list
      fetchGyms();
    } catch (err) {
      console.error(err);
      alert('Action simulation: Toggled status in UI view.');
      // Local fallback toggle
      setGyms(prev => prev.map(g => g.id === gymId ? { ...g, status: nextStatus } : g));
    } finally {
      setActionLoading(false);
    }
  };

  const exportToCSV = () => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Gym Name,Code,Owner Name,Email,Phone,City,Plan Tier,Members Count,Status,Last Check-in\n';
    
    gyms.forEach(g => {
      csvContent += `"${g.name}","${g.code}","${g.ownerName}","${g.ownerEmail}","${g.ownerPhone}","${g.city}","${g.planTier}",${g.membersCount},"${g.status}","${g.lastCheckIn || 'N/A'}"\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fitscan_gyms_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Gym Accounts</h1>
          <p className="text-gray-400 text-sm mt-1">Onboard, modify subscription tiers, and audit gyms.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2.5 bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] rounded-lg text-sm text-gray-300 transition-all hover:text-white"
          >
            <Download size={16} className="mr-2" />
            Export CSV
          </button>
          <Link
            to="/admin/gyms/new"
            className="flex items-center px-4 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors"
          >
            <Plus size={16} className="mr-1.5" />
            Onboard Gym
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {/* Filter and Search Panel */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-5 shadow-lg flex flex-col md:flex-row gap-4 justify-between items-center">
        <form onSubmit={handleSearchSubmit} className="relative w-full md:max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search gyms by name..."
            className="w-full pl-10 pr-4 py-2 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] text-sm"
          />
          <button type="submit" className="absolute left-3 top-2.5 text-gray-500 hover:text-white">
            <Search size={18} />
          </button>
        </form>

        <div className="flex flex-wrap gap-4 w-full md:w-auto justify-end">
          <div className="flex items-center space-x-2 bg-[#0B0F19] border border-[#1E293B] px-3 py-2 rounded-lg">
            <Filter size={14} className="text-gray-400" />
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="bg-transparent border-none text-xs text-gray-300 focus:outline-none focus:ring-0 cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="flex items-center space-x-2 bg-[#0B0F19] border border-[#1E293B] px-3 py-2 rounded-lg">
            <Filter size={14} className="text-gray-400" />
            <select
              value={planTier}
              onChange={(e) => { setPlanTier(e.target.value); setPage(1); }}
              className="bg-transparent border-none text-xs text-gray-300 focus:outline-none focus:ring-0 cursor-pointer"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Table Grid */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48 text-[#6CD800]">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Loading gyms roster...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
                <tr>
                  <th scope="col" className="px-6 py-4">Gym Code & Name</th>
                  <th scope="col" className="px-6 py-4">Owner Info</th>
                  <th scope="col" className="px-6 py-4">Location</th>
                  <th scope="col" className="px-6 py-4">Plan Tier</th>
                  <th scope="col" className="px-6 py-4 text-center">Members</th>
                  <th scope="col" className="px-6 py-4">Status</th>
                  <th scope="col" className="px-6 py-4">Last Check-in</th>
                  <th scope="col" className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {gyms.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-500">No matching gyms found.</td>
                  </tr>
                ) : (
                  gyms.map((g) => (
                    <tr key={g.id} className="hover:bg-[#1E293B]/20">
                      <td className="px-6 py-4">
                        <Link to={`/admin/gyms/${g.id}`} className="block group">
                          <span className="text-[#6CD800] text-xs font-mono font-bold tracking-wider">{g.code}</span>
                          <span className="block text-sm font-semibold text-white group-hover:underline">{g.name}</span>
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className="block text-white font-medium">{g.ownerName}</span>
                        <span className="block text-xs text-gray-400">{g.ownerPhone}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">{g.city || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <span className="text-xs px-2 py-0.5 rounded font-mono uppercase bg-[#1E293B] text-gray-300 border border-gray-700">
                          {g.planTier}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-semibold text-white">{g.membersCount}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          g.status === 'active' 
                            ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20' 
                            : g.status === 'trial'
                              ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                              : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                            g.status === 'active' 
                              ? 'bg-[#6CD800]' 
                              : g.status === 'trial'
                                ? 'bg-yellow-400'
                                : 'bg-red-500'
                          }`}></span>
                          {g.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {g.lastCheckIn ? new Date(g.lastCheckIn).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Link 
                          to={`/admin/gyms/${g.id}`} 
                          title="View Gym Dashboard"
                          className="inline-flex p-1.5 bg-[#1E293B] hover:bg-[#334155] text-gray-300 rounded transition-colors"
                        >
                          <Eye size={14} />
                        </Link>
                        
                        <button
                          onClick={() => handleToggleStatus(g.id, g.status)}
                          disabled={actionLoading}
                          title={g.status === 'suspended' ? 'Reactivate Gym' : 'Suspend Gym'}
                          className={`inline-flex p-1.5 rounded transition-colors ${
                            g.status === 'suspended'
                              ? 'bg-green-500/10 hover:bg-green-500/20 text-green-400'
                              : 'bg-red-500/10 hover:bg-red-500/20 text-red-400'
                          }`}
                        >
                          {g.status === 'suspended' ? <CheckCircle size={14} /> : <Trash2 size={14} />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-between items-center px-6 py-4 bg-[#0B0F19] border-t border-[#1E293B]">
            <span className="text-xs text-gray-400">
              Showing page {page} of {totalPages} ({total} total gyms)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-[#1E293B] hover:bg-[#334155] border border-[#334155] rounded text-xs text-gray-300 disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 bg-[#1E293B] hover:bg-[#334155] border border-[#334155] rounded text-xs text-gray-300 disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GymList;
